import { useState, useRef } from 'react';

const ROW_STYLES = {
  overwrite: 'bg-red-50',
  parsed: 'bg-yellow-50',
  notFound: 'bg-gray-50 opacity-60',
  normal: '',
};

export default function UploadModal({ onClose, onUploadComplete }) {
  const [step, setStep] = useState('select'); // select → preview → result → applied
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [result, setResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/uploads/preview', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data.preview);
      setSummary(data.summary);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/uploads/confirm', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Confirm failed');
      setResult(data);
      setStep('result');
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/uploads/apply', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Apply failed');
      setApplyResult(data);
      setStep('applied');
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      setError('Failed to apply updates: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload ShipRush Report</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'select' && 'Select an XLSX file to preview'}
              {step === 'preview' && 'Review the parsed data before confirming'}
              {step === 'result' && 'Staged — apply updates to finalize'}
              {step === 'applied' && 'Updates applied to orders'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: File select */}
          {step === 'select' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                onClick={() => fileRef.current?.click()}
                className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
              >
                <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {file ? (
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">Click to select file</p>
                    <p className="text-xs text-gray-400 mt-1">.xlsx files only</p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-400 font-medium">Total Rows</p>
                  <p className="text-xl font-bold text-gray-900">{summary.trackingEntries}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-emerald-600 font-medium">Matched</p>
                  <p className="text-xl font-bold text-emerald-700">{summary.matched}</p>
                </div>
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-400 font-medium">Not Found</p>
                  <p className="text-xl font-bold text-gray-500">{summary.notFound}</p>
                </div>
                <div className="bg-red-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-red-500 font-medium">Overwrites</p>
                  <p className="text-xl font-bold text-red-600">{summary.overwrites}</p>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300" />
                  Parsed from batch reference
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-red-100 border border-red-300" />
                  Would overwrite existing tracking
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-gray-100 border border-gray-300 opacity-60" />
                  Order not found in DB
                </span>
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[40vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Tracking #</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Raw Reference</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Order UID</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Status</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Existing Tracking</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.map((row, i) => {
                        let rowStyle = ROW_STYLES.normal;
                        if (!row.found) rowStyle = ROW_STYLES.notFound;
                        else if (row.wouldOverwrite) rowStyle = ROW_STYLES.overwrite;
                        else if (row.parsed) rowStyle = ROW_STYLES.parsed;

                        return (
                          <tr key={i} className={rowStyle}>
                            <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.tracking}</td>
                            <td className="px-4 py-2 text-xs text-gray-500">{row.rawReference || '—'}</td>
                            <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">
                              {row.orderUid || '—'}
                              {row.parsed && (
                                <span className="ml-1.5 text-[10px] font-medium text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded">PARSED</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {row.found ? (
                                <span className="text-emerald-600 font-medium">Matched</span>
                              ) : (
                                <span className="text-gray-400">Not found</span>
                              )}
                              {row.wouldOverwrite && (
                                <span className="ml-1.5 text-[10px] font-medium text-red-600 bg-red-100 px-1.5 py-0.5 rounded">OVERWRITE</span>
                              )}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-400">{row.existingTracking || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Result (staged, awaiting apply) */}
          {step === 'result' && result && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Staged Successfully</h3>
              <p className="text-sm text-gray-500 mb-6">{result.filename}</p>
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{result.rowCount}</p>
                  <p className="text-xs text-gray-400">Total Rows</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{result.recordsMatched}</p>
                  <p className="text-xs text-gray-400">Matched</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-600">{result.recordsCreated}</p>
                  <p className="text-xs text-gray-400">Staged</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-6 max-w-sm text-center">
                {result.recordsCreated} tracking update{result.recordsCreated !== 1 ? 's are' : ' is'} staged as "pending".
                Click <span className="font-semibold text-indigo-600">Apply Updates</span> below to push them to orders.
              </p>
            </div>
          )}

          {/* Step 4: Applied */}
          {step === 'applied' && applyResult && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Updates Applied</h3>
              <p className="text-sm text-gray-500 mb-6">Tracking numbers pushed to orders</p>
              <div className="grid grid-cols-2 gap-8 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{applyResult.applied ?? 0}</p>
                  <p className="text-xs text-gray-400">Applied</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{applyResult.total ?? 0}</p>
                  <p className="text-xs text-gray-400">Total Pending</p>
                </div>
              </div>
              {applyResult.errors?.length > 0 && (
                <p className="text-xs text-red-600 mt-6 max-w-sm text-center">
                  {applyResult.errors.length} error{applyResult.errors.length !== 1 ? 's' : ''} occurred while applying.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {step === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Parsing...' : 'Preview'}
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button
                onClick={() => { setStep('select'); setPreview(null); setSummary(null); setFile(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading || summary?.matched === 0}
                className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : `Confirm & Stage ${summary?.matched || 0} Records`}
              </button>
            </>
          )}
          {step === 'result' && (
            <>
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Close without applying
              </button>
              <button
                onClick={handleApply}
                disabled={loading || !result?.recordsCreated}
                className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Applying...' : `Apply ${result?.recordsCreated || 0} Update${result?.recordsCreated !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'applied' && (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
